import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bot, Zap, Shield, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { toast } from "@/components/ui/Toast";
import { MultiStepLoader } from "@/components/ui/MultiStepLoader";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// MitigationTab
//
// Shows AI and AUTO recommendations for an assessment.
// Allows updating status and generating new recommendations.
// FIX-017: ITEM-026 + ITEM-009 + ITEM-011 + ITEM-030
// ─────────────────────────────────────────────

type RecommendationSource = "AI" | "AUTO";
type RecommendationStatus = "NOT_STARTED" | "IN_PROGRESS" | "IMPLEMENTED";
type RecommendationPriority = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

interface Recommendation {
  id: string;
  assessmentId: string;
  source: RecommendationSource;
  title: string;
  description: string;
  priority: RecommendationPriority;
  status: RecommendationStatus;
  questionId: string | null;
  optionKey: string | null;
  fromPreviousRun: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RecommendationsResponse {
  data: Recommendation[];
}

interface GenerateResponse {
  data: {
    generated: number;
    recommendations: Recommendation[];
  };
}

interface MitigationTabProps {
  assessmentId: string;
}

// ─────────────────────────────────────────────
// AI analysis steps shown in the multi-step loader overlay
// Build function takes t — see buildColumns(t) pattern in AppInventory.tsx
// ─────────────────────────────────────────────
function buildAiLoaderSteps(t: (key: string) => string): string[] {
  return [
    t("assessment.aiLoaderSteps.step1"),
    t("assessment.aiLoaderSteps.step2"),
    t("assessment.aiLoaderSteps.step3"),
    t("assessment.aiLoaderSteps.step4"),
    t("assessment.aiLoaderSteps.step5"),
    t("assessment.aiLoaderSteps.step6"),
    t("assessment.aiLoaderSteps.step7"),
    t("assessment.aiLoaderSteps.step8"),
    t("assessment.aiLoaderSteps.step9"),
    t("assessment.aiLoaderSteps.step10"),
    t("assessment.aiLoaderSteps.step11"),
    t("assessment.aiLoaderSteps.step12"),
    t("assessment.aiLoaderSteps.step13"),
    t("assessment.aiLoaderSteps.step14"),
    t("assessment.aiLoaderSteps.step15"),
    t("assessment.aiLoaderSteps.step16"),
    t("assessment.aiLoaderSteps.step17"),
    t("assessment.aiLoaderSteps.step18"),
  ];
}

const PRIORITY_COLORS: Record<RecommendationPriority, string> = {
  CRITICAL: "var(--danger)",
  HIGH: "var(--k-orange)",
  MEDIUM: "var(--warning)",
  LOW: "var(--success)",
};

function buildStatusLabels(t: (key: string) => string): Record<RecommendationStatus, string> {
  return {
    NOT_STARTED: t("status.notStarted"),
    IN_PROGRESS: t("status.inProgress"),
    IMPLEMENTED: t("status.implemented"),
  };
}

const STATUS_ICONS: Record<RecommendationStatus, React.ReactNode> = {
  NOT_STARTED: <Clock className="h-3.5 w-3.5" />,
  IN_PROGRESS: <AlertCircle className="h-3.5 w-3.5" />,
  IMPLEMENTED: <CheckCircle className="h-3.5 w-3.5" />,
};

const STATUS_COLORS: Record<RecommendationStatus, string> = {
  NOT_STARTED: "var(--text-muted)",
  IN_PROGRESS: "var(--warning)",
  IMPLEMENTED: "var(--success)",
};

function buildPriorityLabels(t: (key: string) => string): Record<RecommendationPriority, string> {
  return {
    CRITICAL: t("risk.critical"),
    HIGH: t("risk.high"),
    MEDIUM: t("risk.medium"),
    LOW: t("risk.low"),
  };
}

function PriorityBadge({ priority }: { priority: RecommendationPriority }) {
  const { t } = useTranslation();
  const color = PRIORITY_COLORS[priority];
  const priorityLabels = buildPriorityLabels(t);
  return (
    <span
      className="inline-flex items-center rounded-badge px-2 py-0.5 text-[11px] font-semibold flex-shrink-0"
      style={{
        backgroundColor: `${color}22`,
        color,
      }}
    >
      {priorityLabels[priority]}
    </span>
  );
}

function StatusDropdown({
  recId,
  assessmentId,
  currentStatus,
}: {
  recId: string;
  assessmentId: string;
  currentStatus: RecommendationStatus;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [localStatus, setLocalStatus] = useState<RecommendationStatus>(currentStatus);

  const updateMutation = useMutation({
    mutationFn: (status: RecommendationStatus) =>
      api.patch(`/assessments/${assessmentId}/recommendations/${recId}`, { status }),
    onSuccess: (_, status) => {
      setLocalStatus(status);
      queryClient.invalidateQueries({ queryKey: ["recommendations", assessmentId] });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("assessment.updateStatusFailed"));
    },
  });

  const statusColor = STATUS_COLORS[localStatus];
  const statusLabels = buildStatusLabels(t);

  return (
    <select
      value={localStatus}
      onChange={(e) => {
        const val = e.target.value as RecommendationStatus;
        updateMutation.mutate(val);
      }}
      disabled={updateMutation.isPending}
      className="rounded-control border border-border-default bg-card px-2 py-1 text-[12px] font-medium transition-colors hover:bg-hover focus:outline-none focus:ring-1 focus:ring-accent"
      style={{ color: statusColor }}
    >
      {Object.entries(statusLabels).map(([value, label]) => (
        <option key={value} value={value} style={{ color: "var(--text-primary)" }}>
          {label}
        </option>
      ))}
    </select>
  );
}

function RecommendationCard({
  rec,
  assessmentId,
}: {
  rec: Recommendation;
  assessmentId: string;
}) {
  const { t } = useTranslation();
  const priorityColor = PRIORITY_COLORS[rec.priority];
  const statusColor = STATUS_COLORS[rec.status];
  const statusLabels = buildStatusLabels(t);

  return (
    <div
      className="rounded-card border border-border-default bg-card p-4"
      style={{ borderLeft: `3px solid ${priorityColor}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px] font-medium text-text-primary leading-snug">
              {rec.title}
            </span>
            {rec.fromPreviousRun && (
              <span
                className="rounded-badge px-1.5 py-0.5 text-[10px] font-semibold flex-shrink-0"
                style={{
                  backgroundColor: "var(--bg-hover)",
                  color: "var(--text-muted)",
                }}
              >
                {t("assessment.fromPreviousRun")}
              </span>
            )}
          </div>
          <p className="text-[13px] text-text-secondary mt-1">{rec.description}</p>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <PriorityBadge priority={rec.priority} />
          <StatusDropdown
            recId={rec.id}
            assessmentId={assessmentId}
            currentStatus={rec.status}
          />
          <div
            className="flex items-center gap-1 text-[11px]"
            style={{ color: statusColor }}
          >
            {STATUS_ICONS[rec.status]}
            <span>{statusLabels[rec.status]}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecommendationSection({
  title,
  icon,
  recommendations,
  assessmentId,
  emptyMessage,
}: {
  title: string;
  icon: React.ReactNode;
  recommendations: Recommendation[];
  assessmentId: string;
  emptyMessage: string;
}) {
  return (
    <div className="rounded-card border border-border-default bg-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <span style={{ color: "var(--accent)" }}>{icon}</span>
        <h3 className="text-[15px] font-semibold text-text-primary">{title}</h3>
        <span
          className="ml-auto rounded-badge px-2 py-0.5 text-[11px] font-semibold"
          style={{
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-muted)",
          }}
        >
          {recommendations.length}
        </span>
      </div>
      {recommendations.length === 0 ? (
        <p className="text-[14px] text-text-muted py-4 text-center">{emptyMessage}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {recommendations.map((rec) => (
            <RecommendationCard key={rec.id} rec={rec} assessmentId={assessmentId} />
          ))}
        </div>
      )}
    </div>
  );
}

export function MitigationTab({ assessmentId }: MitigationTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<RecommendationsResponse>({
    queryKey: ["recommendations", assessmentId],
    queryFn: () => api.get(`/assessments/${assessmentId}/recommendations`),
    enabled: !!assessmentId,
  });

  const generateAutoMutation = useMutation({
    mutationFn: () =>
      api.post<GenerateResponse>(`/assessments/${assessmentId}/recommendations/generate-auto`),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["recommendations", assessmentId] });
      toast.success(
        `${res.data?.generated ?? 0} ${t("assessment.mitigation.autoGeneratedSuffix")}`
      );
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("assessment.mitigation.autoGenerateFailed"));
    },
  });

  const generateAiMutation = useMutation({
    mutationFn: () =>
      api.post<GenerateResponse>(`/assessments/${assessmentId}/recommendations/generate-ai`),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["recommendations", assessmentId] });
      toast.success(
        `${res.data?.generated ?? 0} ${t("assessment.mitigation.aiGeneratedSuffix")}`
      );
    },
    onError: (err: Error) => {
      const msg = err.message || t("assessment.mitigation.aiGenerateFailed");
      toast.error(t("assessment.mitigation.aiGenerateFailedTitle"), msg);
    },
  });

  const recommendations = data?.data ?? [];
  const aiRecs = recommendations.filter((r) => r.source === "AI");
  const autoRecs = recommendations.filter((r) => r.source === "AUTO");

  const statusLabels = buildStatusLabels(t);
  const aiLoaderSteps = buildAiLoaderSteps(t);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-[14px] text-text-muted">{t("assessment.mitigation.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-[14px] text-text-muted">{t("assessment.mitigation.loadFailed")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* AI analysis multi-step loader overlay */}
      <MultiStepLoader isLoading={generateAiMutation.isPending} steps={aiLoaderSteps} />

      {/* Actions bar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-[18px] font-semibold text-text-primary">{t("assessment.mitigation.title")}</h2>
          <p className="text-[13px] text-text-muted">
            {t("assessment.mitigation.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateAutoMutation.mutate()}
            isLoading={generateAutoMutation.isPending}
            leadingIcon={<Zap className="h-4 w-4" />}
          >
            {t("assessment.mitigation.generateAuto")}
          </Button>
          <Button
            size="sm"
            onClick={() => generateAiMutation.mutate()}
            isLoading={generateAiMutation.isPending}
            leadingIcon={<Bot className="h-4 w-4" />}
          >
            {t("assessment.mitigation.generateAi")}
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      {recommendations.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {(["NOT_STARTED", "IN_PROGRESS", "IMPLEMENTED"] as RecommendationStatus[]).map((s) => {
            const count = recommendations.filter((r) => r.status === s).length;
            const color = STATUS_COLORS[s];
            return (
              <div
                key={s}
                className="rounded-card border border-border-default bg-card p-3 flex items-center gap-3"
              >
                <span style={{ color }}>{STATUS_ICONS[s]}</span>
                <div>
                  <p className="text-[20px] font-semibold text-text-primary">{count}</p>
                  <p className="text-[12px] text-text-muted">{statusLabels[s]}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AI Recommendations section */}
      <RecommendationSection
        title={t("assessment.mitigation.aiRecommendations")}
        icon={<Bot className="h-4 w-4" />}
        recommendations={aiRecs}
        assessmentId={assessmentId}
        emptyMessage={t("assessment.mitigation.noAiRecommendations")}
      />

      {/* Automatic Recommendations section */}
      <RecommendationSection
        title={t("assessment.mitigation.autoRecommendations")}
        icon={<Shield className="h-4 w-4" />}
        recommendations={autoRecs}
        assessmentId={assessmentId}
        emptyMessage={t("assessment.mitigation.noAutoRecommendations")}
      />
    </div>
  );
}
